import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useUser, UserButton } from "@clerk/clerk-react";
import dgptLogo from "@/assets/dgptlogo.png";
import { Button } from "@/components/ui/button";
import { useWorkspaceStore } from "@/store/workspace-store";
import { cn } from "@/lib/utils";

/*
 * Shared shell + primitives for every public page: lookbooks, raiseboards,
 * deal profiles, the board directory and the fund portals.
 *
 * These pages run the `public-theme` skin defined in index.css: cool paper,
 * near-black ink, Switzer throughout. The fund owns the page (its name and
 * mark lead the nav); DiligenceGPT signs off quietly in the footer.
 * Everything here is presentation only, no data fetching.
 */

/** Content widths, so every public page lines up on the same few measures. */
export const PAGE_WIDTH = {
  narrow: "max-w-3xl",
  default: "max-w-5xl",
  wide: "max-w-6xl",
} as const;
export type PageWidth = keyof typeof PAGE_WIDTH;

/** Horizontally centred content column at one of the shared widths. */
export function PageContainer({
  width = "default",
  className,
  children,
}: {
  width?: PageWidth;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("mx-auto w-full px-5 sm:px-8", PAGE_WIDTH[width], className)}>
      {children}
    </div>
  );
}

/**
 * Page wrapper: applies the public skin, owns the scroll container and closes
 * every page with the same quiet footer.
 */
export function RaiseBoardShell({
  children,
  footer = true,
  footerNote,
  width = "default",
  footerFlush = false,
}: {
  children: ReactNode;
  /** The page ends on its own band (an ink closing section): no gap above. */
  footerFlush?: boolean;
  /** The password page stands alone and draws its own sign-off. */
  footer?: boolean;
  /** One line on the footer's left, e.g. who shared the page. */
  footerNote?: ReactNode;
  width?: PageWidth;
}) {
  // The app shell locks body scroll; public pages scroll their own root.
  useEffect(() => {
    document.body.classList.add("public-route");
    return () => document.body.classList.remove("public-route");
  }, []);

  return (
    <div className="public-theme flex h-dvh flex-col overflow-y-auto bg-background text-foreground">
      <div className="flex-1">{children}</div>
      {footer && <PublicFooter note={footerNote} width={width} flush={footerFlush} />}
    </div>
  );
}

/** "Powered by DiligenceGPT", the only place our brand appears on a fund's page. */
export function PoweredBy({ className }: { className?: string }) {
  return (
    <a
      href="https://dgpt.io"
      target="_blank"
      rel="noreferrer"
      className={cn(
        "group inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground",
        className
      )}
    >
      <span>Powered by</span>
      <img
        src={dgptLogo}
        alt="DiligenceGPT"
        className="h-[15px] w-auto opacity-60 grayscale transition-[opacity,filter] duration-200 group-hover:opacity-100 group-hover:grayscale-0"
      />
    </a>
  );
}

export function PublicFooter({
  note,
  width = "default",
  flush = false,
}: {
  note?: ReactNode;
  width?: PageWidth;
  flush?: boolean;
}) {
  return (
    <footer className={cn("border-t border-border/80", !flush && "mt-24")}>
      <PageContainer
        width={width}
        className="flex flex-col gap-3 py-7 sm:flex-row sm:items-center sm:justify-between"
      >
        <p className="text-xs text-muted-foreground">{note}</p>
        <PoweredBy />
      </PageContainer>
    </footer>
  );
}

function initialsOf(name?: string): string {
  if (!name) return "·";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "·";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * A deep, quiet ground for a fund without a logo, picked from its name so the
 * same fund is the same colour everywhere and neighbours in a list differ.
 */
const MONOGRAM_HUES = [226, 262, 200, 160, 340, 20, 280, 180];
function monogramColor(name?: string): string {
  let h = 0;
  for (const ch of name ?? "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return `hsl(${MONOGRAM_HUES[h % MONOGRAM_HUES.length]} 38% 28%)`;
}

/**
 * The fund's mark: its logo when it has one, otherwise a monogram set in ink.
 * The monogram is deliberately solid: on a page that is otherwise grey and
 * white, it is the fund's signature.
 */
export function FundMark({
  name,
  logoUrl,
  size = "sm",
  className,
}: {
  name?: string;
  logoUrl?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const [errored, setErrored] = useState(false);
  const dims =
    size === "lg"
      ? "h-12 w-12 rounded-xl text-base"
      : size === "md"
        ? "h-9 w-9 rounded-lg text-[13px]"
        : "h-7 w-7 rounded-md text-[11px]";

  if (logoUrl && !errored) {
    return (
      <img
        src={logoUrl}
        alt=""
        onError={() => setErrored(true)}
        className={cn(dims, "shrink-0 border border-border bg-card object-contain p-0.5", className)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        dims,
        "inline-flex shrink-0 items-center justify-center font-semibold tracking-tight text-white",
        className
      )}
      style={{ backgroundColor: monogramColor(name) }}
    >
      {initialsOf(name)}
    </span>
  );
}

export interface PublicBrand {
  /** The fund (workspace) that owns the page. */
  name: string;
  logoUrl?: string | null;
  /** Where the mark links: the fund's board or portal, when it has one. */
  href?: string;
  /** What this page is within the fund, shown after a slash ("Lookbook"). */
  context?: string;
}

/**
 * Top navigation.
 *
 * On a fund's own page (`brand` set) the fund leads, the way a deal room
 * carries the host's name on the door, and there is no marketing chrome:
 * just the mark, the page's place within the fund, and a quiet sign-in.
 * Without a brand it is DiligenceGPT's own directory nav.
 *
 * `onRequestAuth` opens the board sign-in dialog; the caller owns that dialog
 * so the copy can be specific to what the user was doing.
 */
export function RaiseBoardNav({
  onRequestAuth,
  width = "default",
  brand,
  actions,
  tone = "ink",
  background,
}: {
  onRequestAuth: (reason: string) => void;
  width?: PageWidth;
  brand?: PublicBrand;
  /** Page-level controls that belong beside sign-in (Share, Subscribe…). */
  actions?: ReactNode;
  /**
   * `ink` (default) is the deal-room bar: dark, and continuous with the
   * PublicCover beneath it. `paper` is for pages without a cover.
   */
  tone?: "ink" | "paper";
  /** The fund's cover ground, so the bar and the cover read as one band. */
  background?: CoverBackground | null;
}) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const { activeWorkspaceId } = useWorkspaceStore();

  const goHome = () => {
    if (isSignedIn) {
      navigate(activeWorkspaceId ? `/${activeWorkspaceId}/dashboard` : "/workspaces");
    } else {
      navigate("/");
    }
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-30 border-b backdrop-blur-md",
        tone === "ink"
          ? "ink-scope border-border/60 bg-background"
          : "border-border/70 bg-background/85 supports-[backdrop-filter]:bg-background/70"
      )}
      style={
        tone === "ink" && background?.color
          ? { backgroundColor: coverStyle({ color: background.color })?.backgroundColor }
          : undefined
      }
    >
      <PageContainer width={width} className="flex h-14 items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-6">
          {brand ? (
            <div className="flex min-w-0 items-center gap-2.5">
              {brand.href ? (
                <a
                  href={brand.href}
                  className="flex min-w-0 items-center gap-2.5 rounded-md outline-offset-4 transition-opacity hover:opacity-80"
                >
                  <FundMark name={brand.name} logoUrl={brand.logoUrl} />
                  <span className="truncate text-sm font-semibold tracking-tight">
                    {brand.name}
                  </span>
                </a>
              ) : (
                <span className="flex min-w-0 items-center gap-2.5">
                  <FundMark name={brand.name} logoUrl={brand.logoUrl} />
                  <span className="truncate text-sm font-semibold tracking-tight">
                    {brand.name}
                  </span>
                </span>
              )}
              {brand.context && (
                <>
                  <span aria-hidden className="hidden text-border sm:inline">
                    /
                  </span>
                  <span className="hidden truncate text-sm text-muted-foreground sm:inline">
                    {brand.context}
                  </span>
                </>
              )}
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={goHome}
                className="flex items-center rounded-md transition-opacity hover:opacity-80"
                aria-label="DiligenceGPT"
              >
                <img src={dgptLogo} alt="DiligenceGPT" className="h-6 w-auto" />
              </button>
              <nav className="hidden items-center gap-5 sm:flex">
                <button
                  type="button"
                  onClick={() => navigate("/global")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Raiseboard
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/rb")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Boards
                </button>
              </nav>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {isLoaded && isSignedIn ? (
            <div className="flex items-center gap-3 pl-1">
              <span className="hidden max-w-[12rem] truncate text-sm text-muted-foreground md:inline">
                {user?.firstName
                  ? [user.firstName, user.lastName].filter(Boolean).join(" ")
                  : user?.primaryEmailAddress?.emailAddress ?? ""}
              </span>
              <UserButton
                appearance={{ elements: { avatarBox: "w-7 h-7" } }}
                afterSignOutUrl={
                  typeof window !== "undefined" ? window.location.pathname : "/"
                }
              />
            </div>
          ) : brand ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRequestAuth("Sign in to continue.")}
              className="h-8 px-3 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Sign in
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRequestAuth("Sign in to continue.")}
                className="hidden h-8 px-3 text-sm font-medium text-muted-foreground hover:text-foreground sm:inline-flex"
              >
                Sign in
              </Button>
              <Button
                size="sm"
                onClick={() => onRequestAuth("Sign in to continue.")}
                className="h-8 rounded-full px-4 text-sm"
              >
                Get started
              </Button>
            </>
          )}
        </div>
      </PageContainer>
    </header>
  );
}

/** A short line above a page title: where you are, set small and quiet. */
export function Eyebrow({
  icon,
  children,
  className,
}: {
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground",
        className
      )}
    >
      {icon}
      {children}
    </p>
  );
}

/**
 * The page title. Large, tight and sans: the fund's or lookbook's name is the
 * headline of the page, so it gets the scale.
 */
export function DisplayTitle({
  children,
  className,
  as: Tag = "h1",
}: {
  children: ReactNode;
  className?: string;
  as?: "h1" | "h2";
}) {
  return (
    <Tag
      className={cn(
        "text-balance text-[clamp(2.25rem,1.55rem+2.6vw,3.6rem)] font-semibold leading-[1.02] tracking-[-0.035em]",
        className
      )}
    >
      {children}
    </Tag>
  );
}

/** A quiet bordered surface, for the few things that genuinely sit apart. */
export function RBCard({
  children,
  className,
  padded = true,
  id,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  /** Anchor target, for pages with a section nav. */
  id?: string;
}) {
  return (
    <div
      id={id}
      className={cn(
        "rounded-xl border border-border bg-card",
        padded && "p-5 sm:p-6",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Section heading: small, medium-weight, muted. Structure, not decoration. */
export function SectionLabel({
  children,
  as: Tag = "h2",
  className,
}: {
  children: ReactNode;
  as?: "h2" | "h3" | "p";
  className?: string;
}) {
  return (
    <Tag className={cn("text-[13px] font-medium text-muted-foreground", className)}>
      {children}
    </Tag>
  );
}

/** Neutral topic / metadata tag. */
export function RBTag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-border/80 bg-card px-1.5 py-px text-[12px] font-medium text-foreground/70">
      {children}
    </span>
  );
}

/** "Raising" state: a green dot and a word. The only green on the page. */
export function StatusChip({
  children = "Raising",
  size = "sm",
}: {
  children?: ReactNode;
  size?: "sm" | "md";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 font-medium text-raising",
        size === "md" ? "text-sm" : "text-[12px]"
      )}
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        <span className="absolute inset-0 rounded-full bg-raising/30 motion-safe:animate-ping [animation-duration:2.4s]" />
        <span className="relative h-1.5 w-1.5 rounded-full bg-raising" />
      </span>
      {children}
    </span>
  );
}

/** Dot-separated run of muted metadata. Skips empty values. */
export function MetaRow({
  items,
  className,
}: {
  items: Array<ReactNode | null | undefined | false>;
  className?: string;
}) {
  const parts = items.filter(Boolean) as ReactNode[];
  if (parts.length === 0) return null;
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground",
        className
      )}
    >
      {parts.map((part, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="select-none text-muted-foreground/40">·</span>}
          {part}
        </span>
      ))}
    </div>
  );
}

/**
 * The one shape every empty, filtered-to-nothing and unavailable state takes:
 * a mark, a sentence that says what happened, and at most one way forward.
 */
export function PublicState({
  icon,
  title,
  children,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-start py-14", className)}>
      {icon && (
        <span className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground [&_svg]:h-[18px] [&_svg]:w-[18px]">
          {icon}
        </span>
      )}
      <p className="text-lg font-semibold tracking-tight">{title}</p>
      {children && (
        <div className="mt-1.5 max-w-md text-[15px] leading-relaxed text-muted-foreground">
          {children}
        </div>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/**
 * A fund's choice of cover background: a colour, an image, or both (the
 * colour then tints the image). Null means the house ink.
 */
export interface CoverBackground {
  color?: string | null;
  imageUrl?: string | null;
}

/** Curated dark grounds a fund can pick from; all hold white type. */
export const COVER_PRESETS: Array<{ name: string; color: string | null }> = [
  { name: "Ink", color: null },
  { name: "Midnight", color: "#14213d" },
  { name: "Forest", color: "#123a2b" },
  { name: "Teal", color: "#0f3b40" },
  { name: "Oxblood", color: "#4a1622" },
  { name: "Aubergine", color: "#2e1a3d" },
  { name: "Graphite", color: "#2a2a2c" },
];

/** DiligenceGPT's own pages (global board, directory) use the house violet. */
export const HOUSE_COVER: CoverBackground = { color: "#251a4a" };

const HEX = /^#(?:[0-9a-f]{3}){1,2}$/i;

/**
 * A fund's saved cover: `cover_color` and `background_cover` (an image URL) on
 * its workspace. Read loosely because the public payloads type the workspace
 * differently page to page.
 */
export function fundCover(opportunity: unknown): CoverBackground | null {
  const o = (opportunity ?? {}) as { cover_color?: string | null; background_cover?: string | null };
  if (!o.cover_color && !o.background_cover) return null;
  return { color: o.cover_color ?? null, imageUrl: o.background_cover ?? null };
}

/**
 * Inline style for a cover or nav on a fund's background. The colour is pushed
 * toward black so white type always holds, whatever the fund picked; an image
 * gets a flat tint of that colour (or ink) over it for the same reason.
 */
export function coverStyle(background?: CoverBackground | null): CSSProperties | undefined {
  const color = background?.color && HEX.test(background.color) ? background.color : null;
  const image = background?.imageUrl || null;
  if (!color && !image) return undefined;
  const ground = color
    ? `color-mix(in oklab, ${color} 82%, black)`
    : "hsl(226 34% 7%)";
  if (!image) return { backgroundColor: ground };
  const tint = color
    ? `color-mix(in oklab, ${color} 70%, transparent)`
    : "hsl(226 34% 7% / 0.72)";
  return {
    backgroundColor: ground,
    backgroundImage: `linear-gradient(${tint}, ${tint}), url("${image.replace(/"/g, "%22")}")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  };
}

/**
 * The deal-room cover every public page opens with: one compact band with the
 * page's name, a sentence of context and its actions side by side, on flat
 * ink (or the fund's chosen ground) with the page grid drawn in hairlines.
 */
export function PublicCover({
  eyebrow,
  title,
  size = "xl",
  lede,
  aside,
  children,
  width = "default",
  background,
  leading,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  /** `xl` for a lookbook or fund; `lg` for a company; `md` for a form step. */
  size?: "xl" | "lg" | "md";
  lede?: ReactNode;
  /** Actions and people, on the right on desktop. */
  aside?: ReactNode;
  /** Anything else the cover needs, rendered below the band. */
  children?: ReactNode;
  width?: PageWidth;
  background?: CoverBackground | null;
  /** A mark beside the title (a logo). */
  leading?: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "ink-scope grain relative isolate -mt-px overflow-hidden bg-background [--grain-opacity:0.09]",
        className
      )}
      style={coverStyle(background)}
    >
      <CoverGrid width={width} />
      <PageContainer
        width={width}
        className={size === "md" ? "py-8 sm:py-10" : "py-10 sm:py-12"}
      >
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-12">
          <div className="flex min-w-0 items-start gap-5">
            {leading && <div className="shrink-0 pt-1">{leading}</div>}
            <div className="min-w-0">
              {eyebrow && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11.5px] font-medium uppercase tracking-[0.18em] text-muted-foreground motion-safe:animate-rise [&_svg]:h-3.5 [&_svg]:w-3.5 sm:gap-x-2">
                  {eyebrow}
                </div>
              )}
              <h1
                className={cn(
                  "text-balance font-semibold motion-safe:animate-rise",
                  eyebrow && "mt-3",
                  size === "xl" &&
                    "text-[clamp(2.4rem,1.7rem+2.8vw,4rem)] leading-[0.98] tracking-[-0.045em]",
                  size === "lg" &&
                    "text-[clamp(2.1rem,1.6rem+2vw,3.25rem)] leading-[1] tracking-[-0.04em]",
                  size === "md" &&
                    "text-[clamp(1.75rem,1.4rem+1.4vw,2.5rem)] leading-[1.05] tracking-[-0.035em]"
                )}
                style={{ animationDelay: "60ms" }}
              >
                {title}
              </h1>
              {lede && (
                <div
                  className="mt-3 max-w-[62ch] text-[15.5px] leading-relaxed text-muted-foreground motion-safe:animate-rise sm:text-[16.5px] [&_strong]:font-normal [&_strong]:text-foreground"
                  style={{ animationDelay: "120ms" }}
                >
                  {lede}
                </div>
              )}
            </div>
          </div>
          {aside && (
            <div
              className="flex flex-col gap-4 motion-safe:animate-rise lg:items-end"
              style={{ animationDelay: "160ms" }}
            >
              {aside}
            </div>
          )}
        </div>
        {children}
      </PageContainer>
    </section>
  );
}

/**
 * The cover's one structural detail: the page's own 12-column grid drawn as
 * hairlines, aligned to the content edge, fading out toward the bottom. It
 * says "ledger" without decorating anything.
 */
export function CoverGrid({ width = "default" }: { width?: PageWidth }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
      <div
        className={cn(
          "mx-auto h-full w-full border-x border-white/[0.07] px-5 sm:px-8",
          PAGE_WIDTH[width]
        )}
        style={{
          maskImage: "linear-gradient(to bottom, black 0%, black 55%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, black 55%, transparent 100%)",
        }}
      >
        <div className="grid h-full grid-cols-4 sm:grid-cols-12">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className={cn(
                "border-l border-white/[0.045] first:border-l-0",
                i >= 4 && "hidden sm:block"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** The cover's eyebrow pieces: small caps, separated by quiet dots. */
export function CoverEyebrow({ items }: { items: ReactNode[] }) {
  return (
    <>
      {items.filter(Boolean).map((item, i) => (
        <span key={i} className="inline-flex items-center gap-2">
          {/* No separator on small screens: the run wraps there, and a dot
              would lead the new line. */}
          {i > 0 && (
            <span aria-hidden className="hidden text-muted-foreground/50 sm:inline">
              ·
            </span>
          )}
          {item}
        </span>
      ))}
    </>
  );
}

/**
 * A section's heading on paper: large, tight, with an optional two-digit
 * count set small beside it, and controls (filters, sort) on the right.
 */
export function SectionHeading({
  title,
  count,
  actions,
  className,
  as: Tag = "h2",
}: {
  title: ReactNode;
  count?: number;
  actions?: ReactNode;
  className?: string;
  as?: "h2" | "h3";
}) {
  return (
    <div className={cn("flex flex-wrap items-end justify-between gap-x-6 gap-y-4 pb-6", className)}>
      <Tag className="text-[clamp(1.5rem,1.25rem+0.9vw,2rem)] font-semibold leading-none tracking-[-0.03em]">
        {title}
        {count !== undefined && (
          <span className="ml-3 align-top text-[0.45em] font-medium tabular-nums tracking-normal text-muted-foreground">
            {String(count).padStart(2, "0")}
          </span>
        )}
      </Tag>
      {actions}
    </div>
  );
}

/** "Curated by" people: overlapped initials beside who they are. */
export function PeopleStack({
  people,
  max = 4,
}: {
  people: Array<{ first_name?: string; last_name?: string }>;
  max?: number;
}) {
  const named = people.filter((p) => p.first_name || p.last_name).slice(0, max);
  if (named.length === 0) return null;
  return (
    <div className="flex -space-x-2">
      {named.map((p, i) => (
        <span
          key={i}
          title={[p.first_name, p.last_name].filter(Boolean).join(" ")}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-secondary text-[11px] font-semibold text-foreground/80"
        >
          {`${p.first_name?.[0] ?? ""}${p.last_name?.[0] ?? ""}`.toUpperCase() || "·"}
        </span>
      ))}
    </div>
  );
}
